import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Text from 'components/inputs/text';
import { BTUMultiplierType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useState } from 'react';
import axios from 'utils/api';
import { errorModal, successModal } from 'utils/ui';

export default function BtuMultipliersPage() {
  const dispatch = useAppDispatch();
  const { btuMultipliers } = useAppSelector((state) => state.admin);
  const [localMultipliers, setLocalMultipliers] = useState<BTUMultiplierType[]>(
    []
  );

  useEffect(() => {
    setLocalMultipliers(btuMultipliers);
  }, [btuMultipliers]);

  const updateValue = (code: string, raw: string) => {
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return;
    setLocalMultipliers((prev) =>
      prev.map((m) => (m.code === code ? { ...m, value: parsed } : m))
    );
  };

  const hasChanges =
    JSON.stringify(localMultipliers) !== JSON.stringify(btuMultipliers);

  const handleSave = async () => {
    try {
      const changed = localMultipliers.filter((m) => {
        const original = btuMultipliers.find((o) => o.code === m.code);
        return original && original.value !== m.value;
      });
      await axios.patch(
        'btu-multipliers',
        changed.map((m) => ({ code: m.code, value: m.value }))
      );
      dispatch(fetchDefaultAdminData());
      successModal('Sizing calculator multipliers updated successfully.');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    setLocalMultipliers(btuMultipliers);
  };

  return (
    <div className='mx-auto flex max-w-[800px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>
        Sizing Calculator Multipliers
      </div>
      <div className='flex items-center gap-3'>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={!hasChanges}
        />
        <Button
          text='Reset'
          color='red'
          onClick={handleReset}
          disabled={!hasChanges}
        />
      </div>
      <div className='flex flex-col gap-3'>
        {localMultipliers.map((multiplier) => (
          <div
            key={multiplier.code}
            className='flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
          >
            <div className='w-48 shrink-0'>
              <div className='text-sm font-semibold text-gray-900'>
                {multiplier.label}
              </div>
              <div className='font-mono text-xs text-gray-500'>
                {multiplier.code}
              </div>
            </div>
            <div className='w-40'>
              <Text
                value={String(multiplier.value)}
                onChange={(v) => updateValue(multiplier.code, v)}
                controlled
              />
            </div>
          </div>
        ))}
        {localMultipliers.length === 0 && (
          <div className='py-8 text-center text-gray-500'>
            No multipliers found.
          </div>
        )}
      </div>
    </div>
  );
}
